import { createSlice } from '@reduxjs/toolkit'
import {
  fetchSuppliers,
  addSuppliers,
  fetchAllDropdownsSuppliers,
  createSuppliers,
  exportCSVSuppliers,
  updateSuppliers,
  updateStatusInSuppliers,
  fetchById,
  fetchSupplierItemsAndKycItemName,
  fechCreateSupplier,
  fetchSupplierBySupplierId
} from './thunk'

interface SuppliersState {
  suppliersList: any[]        // <-- flat array of suppliers
  rowCount: number            // total count
  loading: boolean
  error: string | null
  selectedOrganisation: any
  orgDropDown: any[]
  departDropdown: any[]
  orgDropDownLoading: boolean
  orgDropDownError: string | null
  createDepartmentLoading: boolean
  createDepartmentError: string | null
  exportCSVLoading: boolean
  exportCSVError: string | null
  itemOptions: string[]
  createSupplierResult?: {
    message?: string;
    supplier_id?: string;
  };
  itemUnits: {
    name?: string;
    unit?: string;
    item_id?: string
  };
}

const initialState: SuppliersState = {
  suppliersList: [],
  rowCount: 0,
  loading: false,
  error: null,
  selectedOrganisation: null,
  orgDropDown: [],
  orgDropDownLoading: false,
  orgDropDownError: null,
  createDepartmentLoading: false,
  createDepartmentError: null,
  exportCSVLoading: false,
  exportCSVError: null,
  departDropdown: [],
  itemOptions: [] as string[],
  createSupplierResult: undefined,
  itemUnits: {},
}

const supplierSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    setItemUnits: (state, action) => {
      state.itemUnits = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch All Suppliers
      .addCase(fetchSuppliers.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false
        // Keep suppliersList as flat array (matches selector expectation)
        state.suppliersList = Array.isArray(action.payload.data)
          ? action.payload.data
          : []
        // Keep rowCount separate as a number
        state.rowCount = typeof action.payload.rowCount === 'number'
          ? action.payload.rowCount
          : 0
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch suppliers'
      })

      // Fetch Supplier by ID
      .addCase(fetchById.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedOrganisation = action.payload
      })
      .addCase(fetchById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Update Supplier
      .addCase(updateSuppliers.fulfilled, (state, action) => {
        if (!action.payload?.id) return
        state.suppliersList = state.suppliersList.map(org =>
          org.id === action.payload.id ? action.payload : org
        )
      })
      .addCase(updateSuppliers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update supplier'
      })

      // Fetch Organisation Dropdown
      .addCase(fetchAllDropdownsSuppliers.pending, state => {
        state.orgDropDownLoading = true
        state.orgDropDownError = null
      })
      // In reducer.ts
      .addCase(fetchAllDropdownsSuppliers.fulfilled, (state, action) => {
        if (action.payload && action.payload.column && Array.isArray(action.payload.data)) {
          // Store dropdowns by column name
          (state as any)[`${action.payload.column}DropDown`] = action.payload.data;
        }
      })
      .addCase(fetchAllDropdownsSuppliers.rejected, (state, action) => {
        state.orgDropDownLoading = false
        state.orgDropDownError =
          action.error.message || 'Failed to fetch organizations'
      })

      // Create Supplier
      .addCase(createSuppliers.pending, state => {
        state.createDepartmentLoading = true
        state.createDepartmentError = null
      })
      .addCase(createSuppliers.fulfilled, (state, action) => {
        state.createDepartmentLoading = false
        state.createDepartmentError = null
        if (action.payload?.id) {
          state.suppliersList.unshift(action.payload)
          state.rowCount += 1
        }
      })
      .addCase(createSuppliers.rejected, (state, action) => {
        state.createDepartmentLoading = false
        state.createDepartmentError =
          typeof action.payload === 'string'
            ? action.payload
            : action.error.message || 'Failed to create supplier'
      })

      // Export Supplier CSV
      .addCase(exportCSVSuppliers.pending, state => {
        state.exportCSVLoading = true
        state.exportCSVError = null
      })
      .addCase(exportCSVSuppliers.fulfilled, state => {
        state.exportCSVLoading = false
        state.exportCSVError = null
      })
      .addCase(exportCSVSuppliers.rejected, (state, action) => {
        state.exportCSVLoading = false
        state.exportCSVError =
          typeof action.payload === 'string'
            ? action.payload
            : action.error.message || 'Failed to export CSV'
      })
      .addCase(fetchSupplierItemsAndKycItemName.fulfilled, (state, action) => {
        state.itemOptions = Array.isArray(action.payload)
          ? action.payload.map((item: any) => item.item_name)
          : [];
      })
      .addCase(fechCreateSupplier.fulfilled, (state, action) => {
        state.createSupplierResult = {
          message: action.payload?.message,
          supplier_id: action.payload?.supplier_id,
        };
      })
      .addCase(fetchSupplierBySupplierId.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrganisation = action.payload;
      })

  },
})
export const { setItemUnits } = supplierSlice.actions;
export default supplierSlice.reducer